/**
 * PulseWidget — embeddable "Bodenblick Pulse" card for external sites.
 *
 * Route: /widget/pulse
 *
 * - No login, no sidebar.
 * - Compact 320–480 px design, transparent fallback background.
 * - Shows headline metric (top-stress NDVI + wildfire count + season).
 * - Branded footer linking back to bodenblick.com.
 *
 * Embed snippet documented at /embed.
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Fire, FlowerTulip as Bee, Leaf, ArrowRight } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function PulseWidget() {
  const [pulse, setPulse] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/pulse/now`)
      .then((r) => setPulse(r.data))
      .catch((e) => setError(e?.message || "fetch failed"));
  }, []);

  if (error && !pulse) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212] p-4 text-sm text-zinc-400" data-testid="widget-pulse-error">
        Bodenblick · derzeit nicht erreichbar
      </div>
    );
  }
  if (!pulse) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212] p-4" data-testid="widget-pulse-loading">
        <div className="w-full max-w-md animate-pulse space-y-3">
          <div className="h-4 bg-white/10 w-1/3" />
          <div className="h-16 bg-white/10" />
          <div className="h-3 bg-white/10 w-1/2" />
        </div>
      </div>
    );
  }

  const fires = pulse.wildfires;
  const bees = pulse.bees;

  return (
    <>
      <Helmet>
        <title>Bodenblick Pulse</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div
        className="min-h-screen bg-[#121212] p-4 sm:p-5 font-sans"
        data-testid="widget-pulse"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
      >
        <div className="max-w-md mx-auto border border-white/15 bg-[#121212]">
          <div className="flex items-center justify-between border-b border-white/15 px-4 py-2 bg-[#00FA9A] text-black">
            <span className="font-bold text-xs uppercase tracking-[0.18em] flex items-center gap-1.5">
              <Leaf size={12} weight="fill" /> Bodenblick · Pulse
            </span>
            <span className="text-[9px] font-mono opacity-80">live</span>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-white/15 p-2">
                <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">Stress</div>
                <div className="font-bold text-lg text-[#FF7A45]">
                  {pulse.top_stress?.ndvi != null ? (pulse.top_stress.ndvi).toFixed(2) : "—"}
                </div>
                <div className="text-[10px] text-white/60 truncate">{pulse.top_stress?.label || "—"}</div>
              </div>
              <div className="border border-white/15 p-2">
                <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">Brände 24h</div>
                <div className="font-bold text-lg text-white inline-flex items-center gap-1">
                  <Fire size={14} weight="fill" className="text-[#FF6A5A]" />
                  {fires?.europe_24h ?? "—"}
                </div>
                <div className="text-[10px] text-white/60">Europa</div>
              </div>
              <div className="border border-white/15 p-2">
                <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">Bienen</div>
                <div className="font-bold text-lg text-white inline-flex items-center gap-1">
                  <Bee size={14} weight={bees?.active ? "fill" : "regular"} className="text-[#FFC24B]" />
                  {bees?.active ? "an" : "aus"}
                </div>
                <div className="text-[10px] text-white/60">{bees?.avg_temp?.toFixed?.(1) ?? "—"} °C</div>
              </div>
            </div>
            {pulse.recommendation && (
              <div className="border-l-2 border-[#00FA9A]/40 pl-3">
                <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">heute gut für</div>
                <div className="text-sm font-semibold text-white leading-tight">{pulse.recommendation.verb}.</div>
                <div className="text-[11px] text-white/70 leading-snug mt-0.5">{pulse.recommendation.detail}</div>
              </div>
            )}
          </div>
          <a
            href="https://bodenblick.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="block border-t border-white/15 px-4 py-2 bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-[#00FA9A] font-mono flex items-center justify-between hover:bg-[#161616]"
            data-testid="widget-pulse-link"
          >
            <span>bodenblick.com</span>
            <ArrowRight size={11} />
          </a>
        </div>
      </div>
    </>
  );
}
